/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file fleetAccess.js
 * @description
 * Client mirror of the backend guard of `/api/fleet/*` (JWT + `requireRestaurant` +
 * `requirePermission('fleet.view')`). There is NO store-module gate on that surface,
 * so the management tile is gated by role / custom right only. The server stays
 * authoritative (403 `Permission denied: fleet.view`).
 * @module config/fleetAccess
 */

/** Permission the whole `/api/fleet/*` read surface requires. */
export const FLEET_VIEW_PERMISSION = 'fleet.view';

/**
 * Roles that reach `/api/fleet/*`: admin-like roles always; managers / sales /
 * service managers (view + edit + finance); cashier / server / technician /
 * advisor (view + edit); the read-only viewer roles.
 */
export const FLEET_VIEW_ROLES = Object.freeze([
    'system:admin', 'sales:admin', 'restaurantStaff:admin', 'restaurantOwner', 'restaurantAdmin',
    'sales:manager', 'restaurantStaff:manager', 'sales:salesman', 'serviceManager',
    'restaurant:cashier', 'restaurantStaff:cashier', 'restaurant:server', 'restaurantStaff:server',
    'serviceTechnician', 'serviceAdvisor',
    'restaurant:viewer', 'restaurantViewer', 'auditor', 'serviceViewer',
]);

/**
 * Can the user open the fleet area? (role list or the custom `fleet_view` right).
 * @param {{roles?: (Set<string>|string[]), userRights?: object}} [subject] Collected roles + the user's `userRights`.
 * @returns {boolean} True when `fleet.view` is expected to pass.
 */
export const canViewFleet = ({roles, userRights} = {}) => {
    const set = roles instanceof Set ? roles : new Set(Array.isArray(roles) ? roles : []);

    return userRights?.rights?.fleet_view === true || FLEET_VIEW_ROLES.some((role) => set.has(role));
};
